import React from "react";
import { motion } from "framer-motion";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

const EASE = [0.16, 1, 0.3, 1];

/**
 * Section wrapper that fades + lifts itself in once it enters the viewport.
 * `as` lets callers keep semantic tags (section/article/aside). The animation
 * is gated on prefers-reduced-motion: when reduced is requested we render the
 * children in their final state with no motion at all.
 */
export function MotionSection({
  as = "section",
  children,
  className = "",
  id,
  amount = 0.15,
  duration = 0.6,
  ...rest
}) {
  const reduce = usePrefersReducedMotion();
  const MotionTag = motion[as] || motion.section;

  if (reduce) {
    const Tag = as;
    return (
      <Tag id={id} className={className} {...rest}>
        {children}
      </Tag>
    );
  }

  return (
    <MotionTag
      id={id}
      className={className}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount }}
      transition={{ duration, ease: EASE }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/**
 * Use for grids whose children should fade-up sequentially (feature cards,
 * trust pillars, gallery thumbs, FAQ rows). Wrap each child in <MotionItem>.
 */
export function MotionStagger({
  children,
  className = "",
  stagger = 0.08,
  amount = 0.15,
  as = "div",
  ...rest
}) {
  const reduce = usePrefersReducedMotion();
  const MotionTag = motion[as] || motion.div;

  if (reduce) {
    const Tag = as;
    return (
      <Tag className={className} {...rest}>
        {children}
      </Tag>
    );
  }

  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount }}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger } },
      }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

export function MotionItem({ children, className = "", as = "div", ...rest }) {
  const reduce = usePrefersReducedMotion();
  const MotionTag = motion[as] || motion.div;

  if (reduce) {
    const Tag = as;
    return (
      <Tag className={className} {...rest}>
        {children}
      </Tag>
    );
  }

  return (
    <MotionTag
      className={className}
      variants={{
        hidden: { opacity: 0, y: 14 },
        show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
      }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}
